import { InvalidDataTypes } from "@/components/PImport/types/InvalidItem";
import { DataType } from "@/types/enums/DataType";
import { Field } from "@/types/Field";
import { Filter, FilterAdvanced } from "@/types/Filter";
import { DataTypeDetectionService } from "./import-dtype-detection";
import { isValidUrlFieldValue } from "./urlField";
import { isEmptyOperand } from "./filterFieldSwap";
import { normalizeFieldValue } from "./input-validator";
import {
  isMailOptInField,
  isSmsOptInField,
  resolveImportBooleanCell,
} from "./import-boolean-words";
import type { ImportMapper } from "@/components/PImport/types/ImportMapper";
import { normalizeToFullLocale } from "./locale-normalize";
import { partyKeyValue } from "./partyKeyValue";
import { Guest } from "@/types/Guest";
import { Gender } from "@/types/enums/Gender";
import { GuestState } from "@/types/enums/GuestState";
import { EventRowDTO } from "@/types/EventRowDTO";
import { RegistrationType } from "@/types/enums/RegistrationType";
import { CreationFieldsRef } from "@/components/PCreateGuest/PCreateGuest";
import { isAfImageValue } from "@/types/AfImage";
import { Event } from "../../types/Event";
import { DashboardCard } from "@/types/DashboardCard";
import { GuestType } from "@/types/enums/GuestType";

export const NO_VALUE_OPERATORS = ["∅", "⊙"];
export const isNoValueOperator = (op: string) => NO_VALUE_OPERATORS.includes(op);

/**
 * Split any array-valued filter row into one scalar-value row per entry,
 * keeping key + operator. A multi-pick (single/multi-select) filter carries an
 * array; the BACKEND `Filter.value` is a single `String`, so an array reaching
 * a PERSISTED filter (saved named filter) would 400 or silently truncate to the
 * last element under JSON-B binding. The fetch/apply path already does this via
 * `parseFiltersApiRequest`; this flattens at the persist boundary so the stored
 * rows are scalars that the backend's same-key OR-grouping reconstructs.
 * Idempotent on scalar rows. Empty arrays were already coerced to "∅" upstream.
 * A per-element "" (the "—" is-empty pick, which can now coexist with real
 * values in one multi-pick dropdown) becomes its own emptiness OR-row —
 * otherwise it would flatten to a bare ("=", "") that the save cleaner drops,
 * silently losing the "unset" half of a "M OR unset" filter. Option keys are
 * never "" so an empty element is unambiguously the is-empty sentinel.
 *
 * The emptiness operator is derived from the ROW's operator, exactly like the
 * query path does per element (`parseFiltersApiRequest`): "=" → "∅" (is empty),
 * "≠" → "⊙" (is set). Hardcoding "∅" inverted every negated pick — and since
 * same-key rows OR-combine, `≠ ["M", ""]` persisted as `≠ M OR unset`, i.e. it
 * re-admitted the very rows the user excluded. Where the persisted row is a
 * role's data access scope that is a widening, not a cosmetic drift.
 */
const emptyElementOperator = (rowOperator: string): string =>
  rowOperator === "=" ? "∅" : rowOperator === "≠" ? "⊙" : rowOperator;

export const flattenArrayFilterValues = (filters: Filter[]): Filter[] =>
  filters.flatMap((f) =>
    Array.isArray(f.value)
      ? f.value.map((v) =>
          v === ""
            ? { ...f, operator: emptyElementOperator(f.operator), value: "" }
            : { ...f, value: v },
        )
      : [f],
  );

// DataTypes whose filter value cell offers a discrete "—" (not-set) pick that
// emits an empty value: TCCBoolean (BOOLEAN), TCCSingleSelect (SINGLE_SELECT
// plus the CHECK_IN_* enums it backs in filter mode), and TCCMultiSelect
// (MULTI_SELECT / SINGLE_SELECT multi-pick — "—" rides as the "" sentinel inside
// the value array and may coexist with real picks). For these, an empty scalar
// value (or a per-element "") is never a real selection — it always means "match
// unset rows", so coercion turns it into the "∅"/"⊙" emptiness operator.
// GUEST_STATUS / EMAIL_STATUS render their own components and are excluded.
const EMPTY_SENTINEL_DATATYPES = new Set<DataType>([
  DataType.BOOLEAN,
  DataType.SINGLE_SELECT,
  DataType.MULTI_SELECT,
  DataType.CHECK_IN_STATUS,
  DataType.CHECK_IN_LOG_ACTION,
]);

/**
 * Translate the "—" (empty) value pick on a discrete field into the
 * value-less emptiness operators the backend understands:
 *   "=" + ""  → "∅" (is empty / unset → Mongo $in:[null, ""])
 *   "≠" + ""  → "⊙" (is set        → Mongo $nin:[null, ""])
 * A bare ("=", "") would otherwise be dropped by isValidValueForDataType (empty
 * is "invalid") or match a literal empty string — never the null/unset rows the
 * user wants. Only fires for EMPTY_SENTINEL_DATATYPES; everything else (free-text
 * strings mid-typing, etc.) is returned untouched. Idempotent: "∅"/"⊙" pass through.
 */
export function coerceEmptyEqualsOperator(
  operator: string,
  value: string | string[],
  dataType: DataType,
): { operator: string; value: string | string[] } {
  const isEmpty =
    value === "" || (Array.isArray(value) && value.length === 0);
  if (!isEmpty || !EMPTY_SENTINEL_DATATYPES.has(dataType)) {
    return { operator, value };
  }
  if (operator === "=") return { operator: "∅", value: "" };
  if (operator === "≠") return { operator: "⊙", value: "" };
  return { operator, value };
}

export function parseFiltersOperations(dataType: DataType): string[] {
  if (!dataType) {
    return [];
  }

  switch (dataType) {
    case DataType.STRING:
      return ["=", "≠", "~", "∅", "⊙"];
    case DataType.OBJECT_ID:
      return ["=", "≠", "~", "∅", "⊙"];
    case DataType.LONG:
      return ["=", "≠", ">", "≥", "<", "≤", "∅", "⊙"];
    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return ["=", "≠", ">", "≥", "<", "≤", "∅", "⊙"];
    case DataType.BOOLEAN:
      // `≠` is NOT redundant with `= false`: an ordinary stored boolean
      // (`emailOptIn`, `hasOpenRequest`, custom BOOLEAN fields) compares
      // with a bare `$eq` on the backend, so `= Nein` finds rows where
      // `false` was WRITTEN and skips rows with no value there; `≠ Ja`
      // (`$ne:true`) is the only single-row "not X" for those. The two
      // tri-state keys (`FilterParser.isTriStateBoolean` = `checkedIn` and
      // `emailUndeliverable`, mirrored in the consumer/check-in parsers) get
      // `= false` → `$in:[false,null]`, so for THEM `= Nein` and `≠ Ja` are
      // equivalent on the flat lane — `≠` is offered for the others.
      return ["=", "≠", "∅", "⊙"];
    case DataType.EMAIL:
      return ["=", "≠", "~", "∅", "⊙"];
    case DataType.URL:
      return ["=", "≠", "~", "∅", "⊙"];
    case DataType.DATE:
      return ["=", "≠", ">", "≥", "<", "≤", "∅", "⊙"];
    case DataType.DATE_TIME:
      return ["=", "≠", ">", "≥", "<", "≤", "∅", "⊙"];
    // Discrete enum/select fields support "≠" too: the backend builds it as a
    // plain $ne (FilterParser#buildFilterClause) with no per-type restriction —
    // already exercised on state/acceptanceState in FilterParserTest. $ne also
    // matches null/missing, so "Status ≠ X" includes status-less rows (intended).
    // Note for MULTI_SELECT (array field): $ne = "array does not contain X";
    // two ≠ rows on one key OR-combine into a near-tautology — a single-row
    // concern only. BOOLEAN offers ≠ too — see its own case above for why
    // `= false` is not a substitute on a non-tri-state boolean.
    case DataType.SINGLE_SELECT:
      return ["=", "≠", "~", "∅", "⊙"];
    case DataType.GUEST_STATUS:
      return ["=", "≠", "∅", "⊙"];
    case DataType.MULTI_SELECT:
      return ["=", "≠", "~", "∅", "⊙"];
    case DataType.EMAIL_STATUS:
      return ["=", "≠", "∅", "⊙"];
    case DataType.CHECK_IN_LOG_ACTION:
      return ["=", "≠", "∅", "⊙"];
    case DataType.CHECK_IN_STATUS:
      return ["=", "≠", "∅", "⊙"];
    case DataType.COMPANIONS:
      return ["="];
    case DataType.IMAGE:
      // IMAGE stores a backend-minted object — only presence checks make
      // sense (mirrors ConditionBuilder operators.ts).
      return ["∅", "⊙"];
    case DataType.UNKNOWN:
      // B2: events may share an additionalField key across multiple rows with
      // different stored types (e.g. LONG on event A, STRING on event B). The
      // key aggregator collapses multi-type keys to UNKNOWN. Expose string-only
      // operators so the user can still build a filter on the key's raw value.
      return ["=", "≠", "~", "∅", "⊙"];
    default:
      return [];
  }
}

/**
 * Operator a FRESHLY CREATED filter row starts on.
 *
 * Free-text fields default to `~` (contains), everything else to `=`. A filter
 * on a name, an address or a link is almost always a SEARCH — the user knows a
 * fragment, not the stored string — and `=` is Mongo `$eq`, so the untouched
 * default silently answered "no matches" for every partial the box now accepts.
 * Discrete, numeric and date fields keep `=`: there the whole value IS what the
 * user has, and `>`/`<`/option picks are the interesting operators.
 *
 * ID-SHAPED FIELDS are deliberately NOT free-text even where their operator
 * list offers `~`: the backend coerces the operand to a real `ObjectId`, and a
 * `$regex` against a BSON ObjectId matches nothing — a `~` default there would
 * answer "no matches" for every input including a correct, complete id.
 *
 * That carve-out is keyed on the field NAME, not the DataType, because the
 * backend is: `FilterParser.convertValueToAppropriateType` coerces a fixed list
 * of key names, and `EmailSentRepository` carries a second, longer one. The
 * DataType is not a reliable proxy — `senderMailId` is declared STRING here and
 * coerced to an ObjectId there, so a type-keyed guard let the sent-mails
 * sender column default to `~` and return zero rows for every input, where `=`
 * had worked. Keying on the name is what the two sides actually share.
 *
 * Derived through `parseFiltersOperations` rather than a second list, so a type
 * that gains or loses `~` cannot drift away from its own dropdown.
 *
 * ONE type deviates and is left alone on purpose: IMAGE offers `["∅", "⊙"]`
 * only — it stores a backend-minted object, so nothing but a presence check
 * makes sense — and therefore gets a `=` its own dropdown refuses to show. That
 * mismatch predates this function; every row creator hardcoded `=` for every
 * type. Defaulting IMAGE to `∅` instead was tried and REVERTED: `∅` is not a
 * half-built row, it is a COMPLETE one, so a bare column pick in the popup
 * builder became a live, persistable "is empty" filter where the `=` row was
 * dropped by `restoreOriginalFilterKeys` (`isValidOperatorForDataType` refuses
 * `=` on IMAGE). Trading a wrong glyph for a filter nobody asked for is the
 * worse deal. The deviation is pinned in default-filter-operator.test.ts so it
 * cannot become silent.
 */
const CONTAINS_BY_DEFAULT: ReadonlySet<DataType> = new Set([
  DataType.STRING,
  DataType.EMAIL,
  DataType.URL,
]);

/**
 * Field names the backend turns into a BSON `ObjectId` before querying — the
 * union of the key lists in `FilterParser.convertValueToAppropriateType` and
 * `EmailSentRepository.convertValueToAppropriateType`. Their operand is an id,
 * never a fragment, so `~` can only ever answer "no matches" on them.
 *
 * A name list mirroring a name list: if the backend adds a key here, this must
 * learn it, and `default-filter-operator.test.ts` pins the members that are
 * declared as free text on this side (today only `senderMailId`) — those are
 * the ones where nothing but this set stands between the column and a
 * permanently empty result.
 */
const OBJECT_ID_COERCED_KEYS: ReadonlySet<string> = new Set([
  "_id",
  "guestId",
  "eventId",
  "userId",
  "companyId",
  "companionId",
  "mainGuest",
  "programItemId",
  "emailTemplateId",
  "senderMailId",
]);

export function defaultFilterOperator(
  dataType: DataType,
  fieldName?: string,
): string {
  if (fieldName && OBJECT_ID_COERCED_KEYS.has(fieldName)) return "=";
  return CONTAINS_BY_DEFAULT.has(dataType) &&
    parseFiltersOperations(dataType).includes("~")
    ? "~"
    : "=";
}

export function parseField(field: string, allFields: Field[]): Field {
  // Look up the field in FIELDS_EVENT or FIELDS_GUEST array
  const fieldObject = allFields.find((f) => f.name === field);
  if (fieldObject) {
    return fieldObject;
  }
  // Default to string if not found
  return {
    name: field,
    dataType: DataType.UNKNOWN,
    additionalField: true,
    editable: true,
  };
}

export function formatSecondsToMinutes(seconds: number): string {
  const minutes = Math.floor(seconds / 60).toString();
  const remainingSeconds = (seconds % 60).toString();

  return `${minutes.padStart(2, "0")}:${remainingSeconds.padStart(2, "0")}`;
}

export function parseEventAdditionalFields(
  additionalFields: Record<string, string[]>,
): Field[] {
  const fields: Field[] = [];

  for (const [name, values] of Object.entries(additionalFields)) {
    // Determine the dataType based on values length
    let dataType: DataType;

    if (
      values?.length === 1 &&
      Object.values(DataType).includes(values[0] as DataType)
    ) {
      // If there's only one value and it exists in the DataType enum, use it
      dataType = values[0] as DataType;
    } else {
      // If there are multiple values or the single value is not in DataType enum, use unknown
      dataType = DataType.UNKNOWN;
    }

    // Create a Field object
    fields.push({
      name,
      dataType,
      additionalField: true,
      editable: true,
    });
  }

  return fields;
}

/**
 * Creates a translated version of fields, preserving only non-additional fields
 * and setting the additionalField property to false for translation entries
 *
 * @param fields Original field list
 * @param t Translation function
 * @returns Translated field list
 */
export const formatFieldsTranslation = (
  fields: Field[],
  t: (key: string) => string,
): Field[] => {
  return fields.map((field) => {
    // For additionalFields, keep the admin-defined display label (translation)
    // and only fall back to the immutable `name` identifier when a legacy field
    // has none. Overwriting with `name` would leak the raw slug into filter
    // pickers / rows (the identifier is charset-normalized, not the label).
    if (field.additionalField) {
      return {
        ...field,
        translation: field.translation || field.name,
        additionalField: true,
      };
    }

    // For regular fields, apply translation to the name
    return {
      ...field,
      translation: t(field.name), // Translate the field name using the translation function
    };
  });
};

export const restoreOriginalFieldKeys = (
  translatedFields: Field[],
  originalFields: Field[],
  t: (key: string) => string,
): Field[] => {
  // Create a mapping of translated names to original field objects
  const translationMap = new Map<string, Field>();

  // Fill the map with translations of non-additional fields
  originalFields.forEach((originalField) => {
    if (!originalField.additionalField) {
      const translatedName = t(originalField.name);
      translationMap.set(translatedName, originalField);
    }
  });

  return (
    (translatedFields.map((translatedField) => {
      if (translatedField.additionalField) return translatedField;

      // Look up this translated name in our map
      const originalField = translationMap.get(translatedField.name);

      if (originalField) {
        // We found a match, restore the original name and additionalField property
        return {
          ...translatedField,
          name: originalField.name,
          additionalField: originalField.additionalField,
        };
      }
    }) as Field[]) || []
  );
};

// TODO add checks
export const parseToAdvancedFilter = (
  filters: Filter[],
  allFields: Field[],
  t: (key: string) => string,
): FilterAdvanced[] => {
  return filters
    .map((filter) => {
      const field = allFields.filter((field) => {
        if (
          field.additionalField &&
          field.name.startsWith("additionalFields.")
        ) {
          const newName = field.name.replace("additionalFields.", "");
          return filter.key === newName;
        } else {
          return filter.key === field.name;
        }
      })[0];

      if (!field) {
        return null; // Return undefined explicitly for clarity
      }
      if (!field.additionalField && t) field.translation = t(field.name);
      // Additional fields keep their display label; both this (saved-filter key)
      // and formatFieldsTranslation derive translation the same way, so the
      // FilterRow/TriggerRow `key.translation === field.translation` identity
      // match stays consistent.
      else field.translation = field.translation || field.name;
      return {
        key: field,
        operator: filter.operator,
        value: filter.value,
      };
    })
    .filter((item): item is FilterAdvanced => item !== null); // Filter out undefined values
};

export const parseToAdvancedFilterBasic = (
  filters: Filter[],
  field: Field,
): FilterAdvanced[] => {
  const advancedFilters = filters
    .filter((filter) => {
      return filter.key === field.name;
    })
    .map((filter) => {
      return {
        key: field,
        operator: filter.operator,
        value: filter.value,
      };
    });

  // NOTE: rows are intentionally kept 1:1 with the persisted filter rows (no
  // coalescing of same-key select rows into one array cell). The UI-row index
  // must line up with activeFilterState so updateFilter can patch by index; a
  // reloaded saved "value OR unset" therefore shows as separate cells (the value
  // dropdown + a standalone "is empty" cell), matching the popup builders which
  // also don't coalesce. LIVE edits still hold every pick (incl. "—") in ONE
  // array-valued row, so the working dropdown stays unified.
  if (advancedFilters?.length === 0) {
    advancedFilters.push({
      key: field,
      operator: defaultFilterOperator(field.dataType, field.name),
      value: "",
    });
  }

  return advancedFilters;
};

export const getChangedDashboardFields = (
  oldCard: DashboardCard,
  newCard: DashboardCard,
): Partial<Record<keyof DashboardCard, DashboardCard[keyof DashboardCard]>> => {
  const changes: Partial<
    Record<keyof DashboardCard, DashboardCard[keyof DashboardCard]>
  > = {
    dashboardCardId: newCard.dashboardCardId,
  };

  (Object.keys(newCard) as Array<keyof DashboardCard>).forEach((key) => {
    if (key === "dashboardCardId") return; // Skip ID since it's already included

    const oldValue = oldCard[key];
    const newValue = newCard[key];

    // Handle array comparison
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes[key] = newValue;
      }
    } else if (oldValue !== newValue) {
      changes[key] = newValue;
    }
  });

  return changes;
};

/**
 * Validates if a value is valid for a specific data type
 * @param value The value to validate
 * @param dataType The data type to validate against
 * @returns boolean indicating if value is valid
 */
export const isValidValueForDataType = (
  value: unknown,
  dataType: DataType,
): boolean => {
  // If value is null, undefined or empty string, it's invalid
  if (value === null || value === undefined || value === "") {
    return false;
  }

  switch (dataType) {
    case DataType.ID:
    case DataType.CTA:
      return false;

    case DataType.STRING:
    case DataType.EMAIL:
    case DataType.URL:
      return typeof value === "string";

    case DataType.LONG:
      return (
        (typeof value === "number" && Number.isInteger(value)) ||
        (typeof value === "string" && /^-?\d+$/.test(value))
      );

    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return (
        typeof value === "number" ||
        (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value))
      );

    case DataType.BOOLEAN:
      return (
        typeof value === "boolean" ||
        (typeof value === "string" && (value === "true" || value === "false"))
      );

    case DataType.DATE:
    case DataType.DATE_TIME:
      if (typeof value === "string") {
        const date = new Date(value);
        return !isNaN(date.getTime());
      }
      return value instanceof Date;

    case DataType.SINGLE_SELECT:
      // Multi-pick filter rows carry an array (one value per OR-expanded row);
      // a scalar string is still valid for a single-value pick / edit.
      return Array.isArray(value) ? value.length > 0 : typeof value === "string";

    case DataType.MULTI_SELECT:
      return (
        Array.isArray(value) ||
        (typeof value === "string" && value.includes(","))
      );

    case DataType.COMPANIONS:
      return typeof value === "string" && value.length > 0;

    case DataType.IMAGE:
      // IMAGE filter rows are valueless (∅/⊙ only) — no typed value is valid.
      return false;

    case DataType.UNKNOWN:
    default:
      // For unknown types, we assume the value is valid
      return true;
  }
};

/**
 * Checks if an operator is valid for a specific data type
 * @param operator The operator to validate
 * @param dataType The data type to validate against
 * @returns boolean indicating if operator is valid for the data type
 */
export const isValidOperatorForDataType = (
  operator: string,
  dataType: DataType,
): boolean => {
  // If operator is empty or null/undefined, it's invalid
  if (!operator) {
    return false;
  }

  // Get valid operators for this data type
  const validOperators = parseFiltersOperations(dataType);

  // Check if the operator is in the list of valid operators
  return validOperators.includes(operator);
};

/**
 * Restores original filter keys and validates values
 * @param filters The advanced filters to process
 * @returns Simplified filters with invalid values removed
 */
export const restoreOriginalFilterKeys = (
  filters: FilterAdvanced[],
): Filter[] => {
  if (!filters || !Array.isArray(filters)) {
    return [];
  }

  const uniqueFilters: Filter[] = [];
  const filterSignatures = new Set();

  // Filter out invalid entries and transform to simple Filter format
  filters
    // Translate the "—" (empty) pick into the "∅"/"⊙" emptiness operators
    // BEFORE the validity check below — otherwise an ("=", "") row is dropped
    // by isValidValueForDataType (empty counts as invalid) and the filter
    // silently vanishes instead of matching unset rows.
    .map((filter) => {
      const { operator, value } = coerceEmptyEqualsOperator(
        filter.operator,
        filter.value,
        filter.key?.dataType,
      );
      return { ...filter, operator, value };
    })
    .filter((filter) => {
      // Skip filters with missing key
      if (!filter.key || !filter.key.name) {
        return false;
      }

      // Skip filters with invalid operators
      if (!isValidOperatorForDataType(filter.operator, filter.key.dataType)) {
        return false;
      }

      // Skip filters with invalid values
      // Note: For null operators like ∅ (is null) and ⊙ (is not null), the value isn't relevant
      const isNullOperator = filter.operator === "∅" || filter.operator === "⊙";
      if (
        !isNullOperator &&
        !isValidValueForDataType(filter.value, filter.key.dataType)
      ) {
        return false;
      }

      return true;
    })
    .forEach((filter) => {
      const simpleFilter = {
        key: filter.key.name,
        operator: filter.operator,
        value: filter.value,
      };

      // Create a signature to check for duplicates
      const signature = `${simpleFilter.key}|${
        simpleFilter.operator
      }|${JSON.stringify(simpleFilter.value)}`;

      // Only add if this signature hasn't been seen before
      if (!filterSignatures.has(signature)) {
        filterSignatures.add(signature);
        uniqueFilters.push(simpleFilter);
      }
    });

  return uniqueFilters;
};

/**
 * Parses an array of field strings into Field objects with proper data types and translations
 * @param fieldStrings - Array of field strings from TableView
 * @param t - Translation function
 * @param type - Table type ('event' or 'guest')
 * @returns Array of Field objects
 */
export const parseFieldsToFieldObjects = (
  fieldStrings: string[],
  allFields: Field[],
  t?: (key: string) => string,
): Field[] => {
  if (
    !fieldStrings ||
    !Array.isArray(fieldStrings) ||
    fieldStrings?.length === 0
  ) {
    return [];
  }

  return fieldStrings.map((fieldString) => {
    const original = allFields.find((f) => f.name === fieldString);
    if (!original) {
      // If field is not found, create a new Field object with unknown type
      return {
        name: fieldString,
        dataType: DataType.UNKNOWN,
        additionalField: true,
        editable: true,
      };
    }

    // Create a shallow copy to avoid mutating the shared field definition
    const field = { ...original };

    // Non-additional fields translate via i18n; additional fields keep their
    // admin-defined display label (falling back to the identifier for legacy
    // fields) so the guest-list column header shows the label, not the slug.
    if (!field.additionalField && t) {
      field.translation = t(fieldString);
    } else {
      field.translation = field.translation || fieldString;
    }

    return field;
  });
};

export function getRelevantWarning(
  dataType: DataType,
  InvalidDataTypes: InvalidDataTypes,
) {
  switch (dataType) {
    case DataType.BOOLEAN:
      return InvalidDataTypes.boolean!;
    case DataType.DATE:
      return InvalidDataTypes.date!;
    case DataType.DATE_TIME:
      return InvalidDataTypes.dateTime!;
    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return InvalidDataTypes.double!;
    case DataType.EMAIL:
      return InvalidDataTypes.email!;
    case DataType.LONG:
      return InvalidDataTypes.long!;
    case DataType.STRING:
      return InvalidDataTypes.string!;
    default:
      return {
        index: 0,
        value: "",
      };
  }
}

export function parseInvalidEntryMapping(
  value: string,
  replacer: string | string[],
  field?: Field,
): string | string[] {
  if (!field) return replacer;

  switch (field.dataType) {
    case DataType.BOOLEAN: {
      // Shown as what the row will carry ("Nein" → "false"), read with the
      // row builder's own rule — the mail opt-in reads "jo" as a NO — so the
      // check step and the import cannot disagree (audit-14 H-1, M-1).
      const v = resolveImportBooleanCell(value, null, isMailOptInField(field));
      if (v !== null) return String(v);
      // The SMS opt-in's unknown word never takes the column default (audit
      // r1 C8) — the row carries it unset, so the preview says so too (r2 R3-2).
      if (isSmsOptInField(field) && String(value ?? "").trim() !== "") return "NULL";
      return replacer;
    }
    // Validators were transposed (DATE checked isValidDateTimeValue, which
    // requires a time component, so a valid date-only cell was rejected and
    // swapped out for the replacer). DATE → date validity, DATE_TIME → datetime.
    case DataType.DATE:
      return DataTypeDetectionService.isValidDateValue(value)
        ? value
        : replacer;
    case DataType.DATE_TIME:
      return DataTypeDetectionService.isValidDateTimeValue(value)
        ? value
        : replacer;
    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return DataTypeDetectionService.isValidDoubleValue(value)
        ? value
        : replacer;
    case DataType.EMAIL:
      return DataTypeDetectionService.isValidEmailValue(value)
        ? value
        : replacer;
    case DataType.URL:
      return DataTypeDetectionService.isValidUrlValue(value)
        ? value
        : replacer;
    case DataType.LONG:
      return DataTypeDetectionService.isValidLongValue(value)
        ? value
        : replacer;
    case DataType.STRING:
      return DataTypeDetectionService.isValidStringValue(value)
        ? value
        : replacer;
    case DataType.OBJECT_ID:
      return DataTypeDetectionService.isValidObjectIdValue(value)
        ? value
        : "NULL";
    case DataType.SINGLE_SELECT:
      return field.options?.includes(value.trim()) ? value : replacer;
    case DataType.MULTI_SELECT:
      return field.options?.includes(value.trim()) ? value : replacer;
    default:
      return replacer;
  }
}

/**
 * "Nothing there" for ONE custom-field value, the way the write doors read it:
 * `undefined` / `null` / `""` / `[]`. The backend collapses every one of these
 * to `null` before it stores (`InputValidator`), so two blank shapes are the
 * same stored value and must never diff against each other. Shared by the
 * inline lane's clearFields flag ({@link parseGuestsSpecial}) and the drawer's
 * per-key delta ({@link buildUpdateGuests}) so the two lanes agree on what
 * "cleared" means.
 */
export function isBlankAdditionalFieldValue(v: unknown): boolean {
  return (
    v === undefined ||
    v === null ||
    v === "" ||
    (Array.isArray(v) && v.length === 0)
  );
}

export function parseGuestsSpecial(
  currentGuest: Guest,
  updateGuest: Guest,
  field: Field,
  toParsed: string | string[],
) {
  if (field.additionalField) {
    // PB-TI-3: carry ONLY the keys this edit session actually changed.
    //
    // This used to alias the live guest's whole map onto the update, so a
    // one-field inline edit shipped EVERY custom value at its last-rendered
    // value. Combined with the backend's unguarded $set that made a stale tab
    // silently revert a concurrent edit to a DIFFERENT field. (The aliasing was
    // also how PB-RP-3's NaN leaked into the RENDERED guest and re-seeded the
    // cell — the update object must be a pure staging area, never the live
    // row.)
    //
    // Seeding from the UPDATE's own accumulated map, not the guest's, is what
    // makes it a delta: keys edited earlier in the same unsaved session are
    // kept, untouched keys are simply absent.
    //
    // Safe because the update-batch path has always MERGED: GuestService
    // dot-paths each additionalFields key ("patch individual subfields without
    // wiping the existing document"), so an omitted key is left alone rather
    // than deleted. Verified by measurement, not by reading — a pin that omits
    // a key preserves it even against a backend build without the newer
    // single-guest dot-path expansion. Clears are unaffected: they travel as an
    // explicit clearFields $unset below, never as "absent from the map".
    updateGuest.additionalFields = { ...(updateGuest.additionalFields ?? {}) };

    parseGuestAdditionalField(updateGuest, field, toParsed, "");

    // Clearing a custom field leaves its value undefined/empty, which the
    // partial-update merge reads as "no change" (the key is JSON-omitted), so
    // the old value would survive. Carry an explicit $unset of
    // `additionalFields.<name>` via clearFields, mirroring gender/emailOptIn.
    const path = `additionalFields.${field.name}`;
    const cleared = isBlankAdditionalFieldValue(
      updateGuest.additionalFields[field.name],
    );
    const list = updateGuest.clearFields ?? [];
    const idx = list.indexOf(path);
    if (cleared && idx === -1) list.push(path);
    else if (!cleared && idx !== -1) list.splice(idx, 1);
    updateGuest.clearFields = list;
  } else {
    parseGuest(updateGuest, field, toParsed, "");

    // Inline "—" (not-set) clear for clearable base fields: parseGuest leaves
    // gender `undefined` / emailOptIn `null` when emptied, which the partial-
    // update path reads as "no change". Flag an explicit $unset via clearFields
    // (kept in sync as the user toggles values across edits on the same row).
    // NOT smsOptIn (SMS channel wave): the backend's CLEARABLE_FIELDS keeps the
    // strict consent out on purpose — an emptied cell is "no change", never an
    // $unset (guestClearFields.ts; the cell offers no "—" either).
    if (field.name === "gender" || field.name === "emailOptIn") {
      const cleared =
        field.name === "emailOptIn"
          ? updateGuest.emailOptIn == null
          : !updateGuest.gender;
      const list = updateGuest.clearFields ?? [];
      const idx = list.indexOf(field.name);
      if (cleared && idx === -1) list.push(field.name);
      else if (!cleared && idx !== -1) list.splice(idx, 1);
      updateGuest.clearFields = list;
    }
  }
}

/**
 * Defense-in-depth for the guest update-batch payload: drop any
 * `additionalFields` key (and its `additionalFields.<name>` clearFields $unset
 * path) whose custom field no longer exists on the event. `parseGuestsSpecial`
 * copies the guest's whole additionalFields map, so a value left behind by a
 * since-deleted field would otherwise ride along and the backend rejects the
 * whole batch with `unknownAdditionalFieldKey`. Returns a fresh object — the
 * caller must not mutate the live guest's additionalFields (which is aliased).
 */
export function pruneStaleAdditionalFields(
  update: Guest,
  validNames: Set<string>,
): Guest {
  if (!update.additionalFields) return update;
  const cleanedAf: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(update.additionalFields)) {
    if (validNames.has(key)) cleanedAf[key] = value;
  }
  const prefix = "additionalFields.";
  const cleanedClear = update.clearFields?.filter(
    (p) => !p.startsWith(prefix) || validNames.has(p.slice(prefix.length)),
  );
  return { ...update, additionalFields: cleanedAf, clearFields: cleanedClear };
}

export function parseEventsSpecial(
  updateEvent: EventRowDTO,
  field: Field,
  toParsed: string | string[],
) {
  if (field.additionalField) {
    if (!updateEvent.additionalFields) {
      updateEvent.additionalFields = {};
    }
  } else {
    parseEvent(updateEvent, field, toParsed as string);
  }
}

/**
 * additionalFields values are stored as STRINGS everywhere (inline cell edits,
 * drawer saves) — the typed results of parseLong/parseBoolean/parseDouble/
 * parseDate are validation artifacts only. Storing them typed put BSON
 * bool/int64/double/date values into Mongo via the import path (BE pours the
 * map onto guest docs unvalidated), which the string-typed read surfaces then
 * choked on (prod guestlist crash, 2026-07-13). Dates canonicalize to ISO-Z.
 */
function asStoredString(
  parsed: string | number | boolean | Date | undefined,
): string | undefined {
  if (parsed == null) return undefined;
  // PB-RP-3: a non-finite number must never be stringified onto the wire.
  // `String(NaN)` is "NaN", which the backend rejects with 400
  // additionalFieldValueNotFinite — and that 400 refuses the ENTIRE
  // update-batch row, so one cleared cell silently discarded every other edit
  // in the same save and left the old number in place. Returning undefined
  // instead drops the key, which is what the downstream `cleared`
  // (undefined/null/"") check looks for to emit the clearFields $unset.
  // Belt-and-braces: the value path below no longer produces NaN either, but
  // this guard means no future caller can reintroduce the wire literal.
  if (typeof parsed === "number" && !Number.isFinite(parsed)) return undefined;
  return parsed instanceof Date ? parsed.toISOString() : String(parsed);
}

export function parseGuestAdditionalField(
  guest: Guest,
  field: Field,
  toParsed: string | string[],
  defaultValue: string | string[],
) {
  if (!guest.additionalFields) {
    guest.additionalFields = {};
  }

  switch (field.dataType) {
    case DataType.STRING:
      return (guest.additionalFields[field.name] = parseString(
        toParsed as string,
        defaultValue as string,
      ));
    case DataType.LONG:
      return (guest.additionalFields[field.name] = asStoredString(
        parseLong(toParsed as string, defaultValue as string),
      ));
    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return (guest.additionalFields[field.name] = asStoredString(
        parseDouble(toParsed as string, defaultValue as string),
      ));
    case DataType.BOOLEAN:
      return (guest.additionalFields[field.name] = asStoredString(
        parseBoolean(toParsed as string, defaultValue as string),
      ));
    case DataType.EMAIL:
      return (guest.additionalFields[field.name] = parseEmail(
        toParsed as string,
        defaultValue as string,
      ));
    case DataType.URL:
      return (guest.additionalFields[field.name] = parseUrl(
        toParsed as string,
        defaultValue as string,
      ));
    // DATE → parseDate (loose validity, accepts a date-only string like
    // "2026-07-01"); DATE_TIME → parseDateTime (requires a time component).
    // These were transposed: a date-only import cell for a DATE field hit
    // parseDateTime → isValidDateTimeValue false → the value was silently
    // DROPPED. Full ISO-Z values pass both validators, so inline edits were
    // unaffected — only the CSV-import path lost date-only DATE cells.
    case DataType.DATE:
      return (guest.additionalFields[field.name] = asStoredString(
        parseDate(toParsed as string, defaultValue as string),
      ));
    case DataType.DATE_TIME:
      return (guest.additionalFields[field.name] = asStoredString(
        parseDateTime(toParsed as string, defaultValue as string),
      ));
    case DataType.SINGLE_SELECT:
      return (guest.additionalFields[field.name] = parseSelect<string>(
        toParsed as string,
        field.options! as string[],
        defaultValue as string,
      ));
    case DataType.MULTI_SELECT:
      return (guest.additionalFields[field.name] = parseMultiSelect(
        toParsed as string,
        field.options! as string[],
        defaultValue as string[],
      ));
    case DataType.IMAGE:
      // IMAGE values are minted exclusively by the presign→confirm endpoints;
      // this generic parse path supports exactly one mutation: blank = clear.
      // Setting `undefined` mirrors the other types' clear shape so
      // parseGuestsSpecial flags the `additionalFields.<name>` $unset via
      // clearFields (the backend then deletes the object + releases storage).
      // Any non-blank value (e.g. the confirmed {key,contentType,size} object
      // echoed by an upload surface) is left untouched — never coerced,
      // stringified or overwritten here.
      if (
        toParsed === "" ||
        toParsed == null ||
        (Array.isArray(toParsed) && toParsed.length === 0)
      ) {
        return (guest.additionalFields[field.name] = undefined);
      }
      return;
    default:
      return;
  }
}

export function parseGuest(
  guest: Guest,
  field: Field,
  toParsed: string | string[],
  defaultValue: string | string[],
) {
  if (field.additionalField) {
    if (!guest.additionalFields) {
      guest.additionalFields = {};
    }

    return parseGuestAdditionalField(
      guest,
      field,
      toParsed,
      defaultValue as string,
    );

    // TODO handle additional fields
  } else {
    switch (field.name) {
      case "firstName":
        return (guest.firstName = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "lastName":
        return (guest.lastName = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "gender":
        return (guest.gender = parseSelect<Gender>(
          toParsed as string,
          field.options! as Gender[],
          defaultValue as Gender,
          false,
        ));
      case "title":
        return (guest.title = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "jobTitle":
        return (guest.jobTitle = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "company":
        return (guest.company = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "email":
        return (guest.email = parseEmail(
          toParsed as string,
          defaultValue as string,
        ));
      case "phone":
        return (guest.phone = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "mobile":
        return (guest.mobile = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "address":
        return (guest.address = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "address2":
        return (guest.address2 = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "zipCode":
        return (guest.zipCode = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "city":
        return (guest.city = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "country":
        return (guest.country = parseString(
          toParsed as string,
          defaultValue as string,
        ));
      case "language": {
        // Accept exact option matches, but also rescue legacy/short codes
        // ("de", "DE", "de-AT") by normalizing to the canonical full locale
        // instead of silently dropping them to the default. Free text that
        // cannot be resolved ("German") still falls back to the default.
        const rawLang = (toParsed as string)?.trim();
        const normalizedLang =
          rawLang && field.options?.includes(rawLang)
            ? rawLang
            : (normalizeToFullLocale(rawLang) ?? (defaultValue as string));
        return (guest.language = normalizedLang);
      }
      case "emailOptIn":
        // Tri-state: a Yes/No cell maps to true/false (an ambiguous word such
        // as "jo" — lb yes, sq no — to false: no consent is the safe side);
        // an empty or unknown cell takes the column default the mapper shows
        // in the preview; without one it stays null (the "—" / not-set
        // state). The default used to be ignored here, so a column set to
        // "No" imported null and the mail went out (audit-14 H-1, M-1).
        return (guest.emailOptIn = resolveImportBooleanCell(
          toParsed as string,
          defaultValue,
          true,
        ));
      case "smsOptIn":
        // SMS channel wave: the same consent reading as the mail opt-in — an
        // ambiguous word is a NO, an empty cell takes the column default,
        // without one it stays null. Strict consent sends only on `true`.
        // Stricter still (audit r1 C8): a word that is neither yes nor no
        // stays null — never the column default.
        return (guest.smsOptIn = resolveImportBooleanCell(
          toParsed as string,
          defaultValue,
          true,
          !field.additionalField,
        ));
      case "state":
        return (guest.state = parseSelect<GuestState>(
          toParsed as string,
          field.options! as GuestState[],
          defaultValue as unknown,
          true,
        ));
      case "guestId":
        return (guest.guestId = parseObjectId(toParsed as string));
      case "mainGuest":
        // The party link, kept as the RAW string the operator typed — a guest
        // id, a booking code or an email, whichever their sheet holds. It is
        // not parsed into an ObjectId here (parseObjectId would drop every
        // non-id form on the floor) and it does not travel in this field:
        // PImportCore lifts it into the job's `partyKeys` map before submit,
        // because the backend's Guest.mainGuest is an ObjectId and an email in
        // it fails at binding. The producer resolves it and derives guestType
        // from whether it is set.
        //
        // The column's default value applies to a blank cell, like it does for
        // every other string field — "this whole file belongs to one host" is
        // the obvious use of the mapper's default box, and BodyChecker's
        // pre-flight notice already counts it. Ignoring it here promised N
        // companions and delivered N main guests.
        //
        // Not routed through `parseString`: that funnel returns `undefined` on
        // an invalid value and treats a whitespace-only cell as an explicit
        // clear. Neither meaning exists for a party key — it is a lookup
        // string, and "nothing" is simply "".
        //
        // `partyKeyValue`: a trim — and for a key that is an ADDRESS the same
        // cleaning `parseEmail` gives the e-mail column, so the key still
        // equals the address it points at.
        return (guest.mainGuest =
          partyKeyValue(String(toParsed ?? "")) ||
          partyKeyValue(String(defaultValue ?? "")));
      case "partyAlias":
        // The party alias: the label a MAIN-GUEST row gives itself so the
        // companions can name it in `mainGuest` ("Familie Müller"). Raw
        // trimmed text, same reasons as above; lifted into `partyAliases` by
        // PImportCore before submit and never on the wire as a field. Not on
        // the Guest type — it is a per-row instruction, not a guest property —
        // hence the local widening.
        //
        // The column's default value is deliberately IGNORED here, unlike for
        // `mainGuest`: "every row is called X" would make every main guest
        // answer to the same alias and every companion ambiguous. There is no
        // reading of a blanket alias that links anything.
        //
        // Without this case the switch falls to `default: return`, the cell
        // is dropped and the whole column is a silent no-op — the failure
        // parseGuest.mainGuest.test.ts exists to make impossible.
        return ((guest as Guest & { partyAlias?: string }).partyAlias =
          partyKeyValue(String(toParsed ?? "")));
      case "arrivalNotice": {
        // W4 Hop B (N29): the addresses to notify when this row's guest
        // arrives — kept RAW here (split, checked and capped by
        // `parseArrivalNoticeCell`) and lifted off the row by PImportCore
        // before submit; the job carries contact ids, never the addresses.
        // A blank cell takes the column default, like `mainGuest` ("this whole
        // file reports to the same host").
        const cell = typeof toParsed === "string" ? toParsed.trim() : "";
        const fallback = typeof defaultValue === "string" ? defaultValue.trim() : "";
        return ((guest as Guest & { arrivalNotice?: string }).arrivalNotice = cell || fallback);
      }
      // guestType is deliberately NOT importable — see
      // GUEST_IMPORT_TARGET_FIELD_NAMES. Its validity depends on `mainGuest`,
      // and an import row that named a type without one would collide with a
      // stored parent it never mentions. Nothing is lost: the type is derived
      // from whether `mainGuest` above carries a value.
      // Login identity: not normalized HERE, matching the carve-outs in
      // `SKIP_NORMALIZE_KEYS`, `sanitizeGuest.IDENTITY_KEYS` and the backend's
      // `GuestService.bulkUpdateByFilter`. `username` sits behind the partial
      // UNIQUE index (eventId, username), so a silent rewrite can collide two
      // rows an admin believes are distinct — an import that used to succeed
      // would fail with E11000 instead.
      //
      // ⚠ THE CARVE-OUT IS DEAD ON THE IMPORT LANE, and saying otherwise is how
      // it read as a guarantee. `importParse` normalizes EVERY cell of the
      // sheet (both branches, since the Excel/CSV asymmetry was closed) and it
      // has to: the column→field mapping does not exist yet at that point, and
      // the preview and the dedupe gate both read those cells. So by the time
      // `parseGuest` is reached from `importer.ts`, `toParsed` is already
      // normalized and these two arms preserve nothing.
      //
      // What the carve-out still buys is the OTHER callers of `parseGuest`,
      // which pass a value straight from the form.
      //
      // Left as-is rather than plumbed through, deliberately: prod was measured
      // to carry no padded `username` / `code`, so there is nothing to repair
      // today, and making it real means threading a raw copy of every cell
      // through the whole import wizard. The residual exposure is a future
      // sheet holding two usernames that differ only by padding, which would
      // now collide with E11000 instead of importing as two rows.
      case "username":
        return (guest.username = parseIdentityString(
          toParsed as string,
          defaultValue as string,
        ));
      case "code":
        return (guest.code = parseIdentityString(
          toParsed as string,
          defaultValue as string,
        ));
      default:
        return;
    }
  }
}

export function parseEvent(
  event: EventRowDTO,
  field: Field,
  toParsed: string | string[],
) {
  if (field.additionalField) {
    if (!event.additionalFields) {
      event.additionalFields = {};
    }

    // TODO handle additional fields
  } else {
    switch (field.name) {
      case "name":
        return (event.name = parseString(toParsed as string, ""));
      case "registrationType":
        return (event.registrationType = parseSelect<RegistrationType>(
          toParsed as string,
          field.options! as RegistrationType[],
          RegistrationType.LOCKED,
        ));
      case "locationName":
        return (event.locationName = parseString(toParsed as string, ""));
      case "address":
        return (event.address = parseString(toParsed as string, ""));
      case "address2":
        return (event.address2 = parseEmail(toParsed as string, ""));
      case "zipCode":
        return (event.zipCode = parseString(toParsed as string, ""));
      case "city":
        return (event.city = parseString(toParsed as string, ""));
      case "country":
        return (event.country = parseString(toParsed as string, ""));
      case "start":
        return (event.start = parseDateTime(toParsed as string, new Date()));
      case "end":
        return (event.end = parseDateTime(toParsed as string, new Date()));
      case "timeZone":
        return (event.timeZone = parseString(toParsed as string, "utc"));
      default:
        return;
    }
  }
}

export function formatToStringFromDataType(
  value: string | string[],
  dataType: DataType,
  clock: string,
  // Event IANA tz for DATE_TIME rendering. Without it these read-only chips
  // (filter chips, exports, role-template preview) formatted in the BROWSER tz
  // while the editors (TCCDate/TCCDateTime) render in the event tz — the same
  // instant then showed two different times/days. DATE is a pure calendar day
  // and is always framed in UTC (matching TCCDate), so tz is irrelevant there.
  timeZone?: string,
): string {
  if (Array.isArray(value)) {
    return value.join(",");
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (dataType === DataType.DATE) {
    const date = parseToUTCDate(value);
    if (!date) return "";

    return parseDateString(date, "de-DE", "UTC");
  }

  if (dataType === DataType.DATE_TIME) {
    const date = parseToUTCDate(value);
    if (!date) return "";
    return parseDateTimeString(date, clock, "de-DE", timeZone);
  }

  return value;
}

/**
 * Parse an ISO string to a Date object, ensuring UTC handling.
 * Backend currently always sends Instants serialized with a trailing Z, so the
 * Z-append below is a no-op in the happy path; it stays as a defensive fallback
 * so any future caller that forgets the Z doesn't silently get local-time semantics.
 */
export function parseToUTCDate(
  isoString: string | null | undefined,
): Date | undefined {
  if (!isoString) return undefined;
  // Untyped callers (filter-condition values, additional-field data) can pass
  // non-strings; a truthy number would throw on .trim below.
  if (typeof isoString !== "string") return undefined;
  // Strip a trailing IANA zone-region suffix (e.g. "2026-05-30T15:31:00Z[UTC]"),
  // which JSON-B/Yasson appends when serializing a java.util.Date. JS `new Date`
  // returns NaN for it, which previously blanked the date picker after a reload.
  const trimmed = isoString.trim().replace(/\[[^\]]*\]$/, "");
  // A bare date ("YYYY-MM-DD") — used for day bounds — must become a full
  // UTC-midnight instant. Appending a bare "Z" ("YYYY-MM-DDZ") is outside the
  // ES date-time grammar: V8 tolerates it but JSC/Safari returns Invalid Date,
  // which would silently drop the bound. Add the time component explicitly.
  const utcString = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
    ? `${trimmed}T00:00:00Z`
    : trimmed.endsWith("Z")
      ? trimmed
      : trimmed + "Z";
  const date = new Date(utcString);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function parseDateString(
  date: Date | undefined,
  locale: string = "de-DE",
  timeZone?: string,
): string {
  if (!date) return "";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function parseDateTimeString(
  date: Date | undefined,
  clock: string,
  locale: string = "de-DE",
  timeZone?: string,
  tzSuffix?: boolean,
): string {
  if (!date) return "";

  const dateString = parseDateString(date, locale, timeZone);

  const timeString = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).format(date);

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const suffix =
    tzSuffix && timeZone && timeZone !== browserTz ? ` · ${timeZone}` : "";

  // Locales without a clock word (en-*: "09:00", not "09:00 o'clock") pass an
  // empty string — join WITHOUT the separator space so no "(09:00 )" artifact
  // remains. Non-empty clock output (de "09:00 Uhr") is byte-identical.
  const timePart = clock ? `${timeString} ${clock}` : timeString;
  return `${dateString} (${timePart}${suffix})`;
}

/**
 * Parses a string value to ensure it's a valid string
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Valid string or fallback/default value
 */
export function parseString(
  value: string,
  fallback: string,
): string | undefined {
  // Normalize BEFORE the validity gate. This is the single funnel for every
  // STRING built-in and every STRING additionalFields value on the inline-cell,
  // create-dialog and import lanes — and the reason `=` filters were broken in
  // prod: `parseFiltersApiRequest` trims the filter OPERAND, so a stored
  // "Künstler 2026 " could never be matched by "=" (Mongo $eq) with any input
  // the user was able to type. `~` ($regex) still matched, which is what made
  // it read as "exact search is broken" instead of "the value has a space".
  const normalized =
    typeof value === "string" ? normalizeFieldValue(value) : value;

  if (normalized && DataTypeDetectionService.isValidStringValue(normalized)) {
    return normalized;
  }

  // A value that WAS present but became empty THROUGH NORMALIZATION is an
  // explicit clear: the user blanked the cell with spaces. Return "" (which
  // parseGuestsSpecial reads as the clearFields $unset) rather than resurrecting
  // the field's previous/default value.
  //
  // The `normalized === ""` half is load-bearing. The gate above is
  // `isValidStringValue`, which rejects emoji, dingbats (U+2600-27BF) and C0/C1
  // control characters — NOT only whitespace. Without it, typing an emoji into
  // an inline cell fell through here and DELETED the field's existing value,
  // and the import mapper's on-screen promise ("invalid values are replaced
  // with the default value") silently became "invalid values are blanked".
  // An invalid value must keep the pre-wave behaviour: take the fallback.
  if (typeof value === "string" && value.length > 0 && normalized === "") {
    return "";
  }

  // Check if fallback is a valid string
  if (DataTypeDetectionService.isValidStringValue(fallback)) {
    return fallback;
  }
}

/**
 * `parseString` without the edge-whitespace normalization, for the two login
 * identity columns. Same validity gate and same fallback behaviour — the only
 * difference is that the stored bytes are left exactly as supplied.
 */
export function parseIdentityString(
  value: string,
  fallback: string,
): string | undefined {
  if (value && DataTypeDetectionService.isValidStringValue(value)) {
    return value;
  }
  if (DataTypeDetectionService.isValidStringValue(fallback)) {
    return fallback;
  }
}

export function parseObjectId(value: string): string | undefined {
  if (value && DataTypeDetectionService.isValidObjectIdValue(value)) {
    return value;
  }

  return undefined;
}

/**
 * Parses a string value to a number (long/integer)
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Parsed number or fallback/default value
 */
export function parseLong(
  value: string,
  fallback: number | string,
): number | undefined {
  const trimmed = String(value).trim();

  if (value && DataTypeDetectionService.isValidLongValue(trimmed)) {
    return finiteOrUndefined(parseInt(trimmed, 10));
  }

  // Check if fallback is a valid integer
  if (Number.isInteger(fallback)) {
    return finiteOrUndefined(fallback as number);
  }

  // PB-RP-3: defensive only. Unlike VALID_DOUBLE_PATTERN, VALID_LONG_PATTERN
  // is /^-?\d+$/ and requires a digit, so LONG never had the empty-string
  // hole. Guarded anyway so both numeric parsers carry the same contract:
  // they return a finite number or nothing.
  if (DataTypeDetectionService.isValidLongValue(fallback as string))
    return finiteOrUndefined(parseInt(fallback as string, 10));
}

/**
 * Parses a string value to a double/float
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Parsed double or fallback/default value
 */
export function parseDouble(
  value: string,
  fallback: number | string,
): number | undefined {
  const trimmed = String(value).trim();

  if (value && DataTypeDetectionService.isValidDoubleValue(trimmed)) {
    return finiteOrUndefined(parseFloat(trimmed));
  }

  // Fallback: accept any numeric (not only integers) and double-shaped strings.
  if (typeof fallback === "number") {
    return finiteOrUndefined(fallback);
  }

  if (DataTypeDetectionService.isValidDoubleValue(fallback as string))
    return finiteOrUndefined(parseFloat(fallback as string));
}

/**
 * PB-RP-3 — clearing a DOUBLE/CURRENCY cell used to yield NaN, not "empty".
 *
 * The inline editor commits "" with an empty fallback, and
 * `DataTypeDetectionService.VALID_DOUBLE_PATTERN` is `/^-?\d*\.?\d*$/` —
 * every quantifier is `*`, so the EMPTY STRING matches it (as do "-", "." and
 * "-."). `parseFloat("")` is then NaN, which flowed on as a value rather than
 * as "the user cleared this".
 *
 * Undefined is the honest answer: no parsable number. It is also what the
 * clear plumbing already recognises. The regex itself is the deeper root and
 * is shared with CSV column-type detection, so it is left alone here — see the
 * PB-RP-3 ledger row for that follow-up.
 */
function finiteOrUndefined(n: number | undefined): number | undefined {
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}

/**
 * Parses a string value to a boolean
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Parsed boolean or fallback/default value
 */
/**
 * Tri-state boolean coercion for nullable boolean fields (e.g. guest
 * `emailOptIn`). "true"/true → true, "false"/false → false, everything else
 * (empty string, null, undefined, unrecognised) → null. This lets the UI's
 * empty "—" option persist as a real null instead of collapsing to false.
 */
export function parseTriStateBoolean(
  value: string | boolean | null | undefined,
): boolean | null {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return null;
}

export function parseBoolean(
  value: string,
  fallback: boolean | string | null | undefined,
): boolean | undefined {
  // The import's ONE word list (import-boolean-words.json): today's
  // true/yes/1/y · false/no/0/n plus every cockpit language's Yes/No and a
  // localised Excel's TRUE/FALSE (⚑14). An empty or unknown cell takes the
  // fallback — the mapper's column default, which arrives as the STRING
  // "true"/"false" (TCCBoolean) and used to be dropped by a typeof-boolean
  // test (audit-14 H-1).
  return resolveImportBooleanCell(value, fallback, false) ?? undefined;
}

/**
 * The check step's preview of one mapped cell (BodyChecker), from the same
 * rules as the row builder: an empty cell shows the column default (or
 * "NULL"), a non-empty one goes through {@link parseInvalidEntryMapping} —
 * for BOOLEAN, what the row will carry ("true"/"false") or the default.
 */
export function previewImportCell(
  value: string | null | undefined,
  mapEntry: ImportMapper | undefined,
): string {
  const fallback = mapEntry?.value ? mapEntry.value : "NULL";
  if (!value) {
    // parseGuest ignores a default on the alias column (a blanket alias links
    // nothing), so the preview must not promise one.
    if (
      mapEntry?.field?.name === "partyAlias" &&
      mapEntry.field.additionalField === false
    ) {
      return "NULL";
    }
    if (mapEntry?.field?.dataType === DataType.BOOLEAN) {
      const v = resolveImportBooleanCell(
        "",
        mapEntry.value,
        isMailOptInField(mapEntry.field),
      );
      return v === null ? "NULL" : String(v);
    }
    return String(fallback);
  }
  // String() keeps a MULTI_SELECT default as the preview always printed it.
  return String(parseInvalidEntryMapping(value, fallback, mapEntry?.field));
}

/**
 * Parses a string value to ensure it's a deliverable email.
 *
 * The value is CLEANED first (`DataTypeDetectionService.cleanEmailValue` —
 * the rule's `cleanEmail` and nothing else: invisible code points dropped
 * anywhere, whitespace off the edges, line breaks at the edges included; an
 * inner line break is NOT folded to a space, it stays and fails) and the
 * clean value is what is returned — a cell that is deliverable once cleaned
 * is uploaded clean, never with the U+200B that made SES refuse it. A cell
 * the rule still refuses ("jürgen@…") is dropped for the (equally cleaned)
 * fallback.
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @returns Clean deliverable email string, the clean fallback, or undefined
 */
export function parseEmail(
  value: string,
  fallback: string,
): string | undefined {
  const cleaned =
    typeof value === "string" ? DataTypeDetectionService.cleanEmailValue(value) : "";
  if (cleaned !== "" && DataTypeDetectionService.isValidEmailValue(cleaned)) {
    return cleaned;
  }

  // Check if fallback is a deliverable email
  const cleanedFallback =
    typeof fallback === "string"
      ? DataTypeDetectionService.cleanEmailValue(fallback)
      : "";
  if (cleanedFallback !== "" && DataTypeDetectionService.isValidEmailValue(cleanedFallback)) {
    return cleanedFallback;
  }
}

/**
 * Parses a string value to ensure it's a valid URL-field value (a bare http(s)
 * URL or the canonical `[label](url)` markdown form).
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @returns Valid URL-field string or fallback, else undefined
 */
export function parseUrl(
  value: string,
  fallback: string,
): string | undefined {
  const trimmed = (value ?? "").trim();

  if (trimmed && isValidUrlFieldValue(trimmed)) {
    return trimmed;
  }

  if (fallback && isValidUrlFieldValue(fallback)) {
    return fallback;
  }
}

/**
 * Parses a string value to a Date object (date only)
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Date object or fallback/default value
 */
export function parseDate(
  value: string,
  fallback: Date | string,
): Date | undefined {
  if (value && !DataTypeDetectionService.isValidDateValue(value)) return;
  const trimmed = value.trim();

  if (DataTypeDetectionService.isValidDateValue(trimmed)) {
    return new Date(trimmed);
  }

  // Check if fallback is a valid date
  if (fallback instanceof Date && !isNaN(fallback.getTime())) {
    return fallback;
  }

  if (DataTypeDetectionService.isValidDateTimeValue(fallback as string)) {
    return new Date(fallback);
  }
}

export function parseToLocalDate(value?: string): Date | undefined {
  if (!value) return;

  if (!DataTypeDetectionService.isValidDateValue(value)) return;

  return new Date(Date.parse(value as string));
}

/**
 * Parses a string value to a Date object (date and time)
 * @param value The string value to parse
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Date object or fallback/default value
 */
export function parseDateTime(
  value: string,
  fallback: Date | string,
): Date | undefined {
  const trimmed = value.trim();

  if (DataTypeDetectionService.isValidDateTimeValue(trimmed)) {
    return new Date(trimmed);
  }

  // Check if fallback is a valid date
  if (fallback instanceof Date && !isNaN(fallback.getTime())) {
    return fallback;
  }

  if (DataTypeDetectionService.isValidDateTimeValue(fallback as string)) {
    return new Date(fallback);
  }
}

/**
 * Parses a value and selects from provided options
 * @param value The string value to check against options
 * @param options Array of valid options to choose from
 * @param fallback Fallback value if parsing fails
 * @param defaultValue Default value if fallback is also invalid
 * @returns Selected option or fallback/default value
 */
export function parseSelect<T>(
  value: string,
  options: T[],
  fallback: unknown,
  required = true,
): T | undefined {
  if (options?.length < 1) throw new Error("validationError");
  const trimmed = value.trim();

  // Convert options to strings for comparison
  const stringOptions = options.map((option) => String(option));

  // Look for exact match
  const indexOfValue = stringOptions.findIndex(
    (option) => option.toLowerCase() === trimmed.toLowerCase(),
  );

  if (indexOfValue !== -1) {
    return options[indexOfValue];
  }

  // Check if fallback is in options
  const fallbackStr = String(fallback);
  const indexOfFallback = stringOptions.findIndex(
    (option) => option.toLowerCase() === fallbackStr.toLowerCase(),
  );

  if (indexOfFallback !== -1) {
    return options[indexOfFallback];
  }

  if (required) return options[0];
}

/**
 * Parses a comma-separated string of values into an array of valid options.
 * For each input value, tries to find a match in the options array.
 * If a match is found, it's included in the result.
 * If no match is found for a value, it's ignored.
 * If no valid options are found at all, returns the fallback values.
 * If fallback contains invalid options, those are filtered out.
 * If after all this the result is empty, returns the first option.
 *
 * @param value - Comma-separated string of values to parse
 * @param options - Array of valid options
 * @param fallback - Fallback values to use if no valid options are found
 * @returns Array of matched options
 */
export function parseMultiSelect(
  value: string | string[],
  options: string[],
  fallback: string | string[],
): string[] {
  if (options?.length < 1) throw new Error("validationError");

  // The MULTI_SELECT table cell (TCCMultiSelect) emits a `string[]` of the
  // selected option keys, while CSV/import paths pass a comma-separated
  // string. Normalise both shapes to a trimmed string[] before matching so
  // `value.split` can never be called on an array (the source of the inline
  // multi-select edit crash on the guestlist).
  const toList = (v: string | string[]): string[] =>
    (Array.isArray(v) ? v : (v ?? "").split(","))
      .map((val) => String(val).trim())
      .filter((val) => val.length > 0);

  const values = toList(value);

  // Find exact matches for each value
  const result = values
    .map((val) => {
      const index = options.findIndex((option) => option === val);
      return index !== -1 ? options[index] : null;
    })
    .filter((val): val is string => val !== null);

  // An explicitly empty selection (user deselected everything) is a valid
  // state for MULTI_SELECT — return it as-is rather than re-injecting the
  // fallback / first option. Only consult the fallback when the input itself
  // was empty (e.g. an unset value with a configured default).
  if (result.length === 0 && values.length === 0) {
    // Filter fallback to only include valid options
    const validFallback = toList(fallback).filter((val) =>
      options.includes(val),
    );

    if (validFallback.length > 0) {
      return validFallback;
    }
  }

  return result;
}

export function getRelevantFields(
  fieldNames: string[],
  allFields: Field[],
): Field[] {
  // Drop names absent from `allFields` (e.g. a built-in field a CUSTOM role's
  // fieldAllowlist excludes, or one like `guestType` that isn't allowlistable)
  // instead of leaving `undefined` holes — consumers map over the result and
  // would crash on `field.name`.
  return fieldNames
    .map((fieldName) => allFields.find((f) => f.name === fieldName))
    .filter((field): field is Field => field !== undefined);
}

export function parseSortByApiRequest(
  fields: string[],
  allFields: Field[],
): string | undefined {
  // Find matching fields and apply prefix if needed
  const processedFields = fields
    .map((fieldName) => {
      const fieldDef = allFields.find((f) => f.name === fieldName);
      // Skip if field not found in allFields
      if (!fieldDef) {
        return null;
      }

      // Apply prefix for additional fields
      return fieldDef.additionalField
        ? `additionalFields.${fieldName}`
        : fieldName;
    })
    .filter((field): field is string => field !== null);

  // Throw error if no valid fields
  if ((processedFields?.length ?? 0) === 0) {
    return undefined;
  }

  // Join fields with commas
  return processedFields[0];
}

/**
 * Parses field names from a request, prefixing "additionalFields." to fields
 * where additionalField is true, and concatenating all fields with commas.
 *
 * @param fields - Array of field names to parse
 * @param allFields - Array of all available Field objects
 * @returns Comma-separated string of field names with appropriate prefixes
 * @throws Error if no valid fields are found
 */
export function parseFieldsApiRequest(
  fields: string[],
  allFields: Field[],
): string {
  // Defensive: callers sometimes pass undefined via a `!` assertion.
  if (!Array.isArray(fields)) return "";
  // Find matching fields and apply prefix if needed
  const processedFields = fields
    .map((fieldName) => {
      const fieldDef = allFields.find((f) => f.name === fieldName);
      // Skip if field not found in allFields
      if (!fieldDef) {
        return null;
      }

      // Apply prefix for additional fields
      return fieldDef.additionalField
        ? `additionalFields.${fieldName}`
        : fieldName;
    })
    .filter((field): field is string => field !== null);

  // Throw error if no valid fields
  if (processedFields?.length === 0) {
    return "";
  }

  // Join fields with commas
  return processedFields.join(",");
}

export function parseFiltersAdvancedApiRequest(
  filters: FilterAdvanced[],
): Filter[] {
  if (!filters || filters.length === 0) return [];

  const parsedFilters = filters.flatMap((filter) => {
    const field = filter.key;

    const key =
      field.additionalField && !field.name.startsWith("additionalFields.")
        ? `additionalFields.${field.name}`
        : field.name;

    // Expand a multi-pick array into one scalar row per value (the backend
    // Filter.value is a single String; same-key rows OR-combine server-side),
    // coercing the "" sentinel ("—" is-empty pick) to "∅"/"⊙" PER element so it
    // becomes its own OR-row alongside real values. Mirrors parseFiltersApiRequest.
    // Operands are normalized here for the same reason `parseFiltersApiRequest`
    // does it: `=` is an exact Mongo `$eq` and the write lanes store the
    // normalized form. This serializer never trimmed at all, so the SAME saved
    // filter could select different rows in the guest list (trimmed) than in the
    // export and bulk-edit it feeds (untrimmed).
    const norm = (v: string) =>
      typeof v === "string" ? normalizeFieldValue(v) : v;

    if (Array.isArray(filter.value)) {
      return filter.value.map((v) => {
        const { operator, value } = coerceEmptyEqualsOperator(
          filter.operator,
          norm(v),
          field.dataType,
        );
        return { key, operator, value };
      });
    }

    // "—" (empty) pick → "∅"/"⊙" emptiness operator on discrete fields.
    const { operator, value } = coerceEmptyEqualsOperator(
      filter.operator,
      Array.isArray(filter.value) ? filter.value : norm(filter.value as string),
      field.dataType,
    );

    return [{ key, operator, value }];
  });

  // Drop a row whose operand is still empty — the same rule
  // `restoreOriginalFilterKeys` applies on the saved-filter lane, and for the
  // same reason: an empty operand is an UNFINISHED row, not a query.
  //
  // It matters more here than there, because these three hosts (PBulkEdit,
  // PExport, PreviewFilterModal) turn the row into a TARGET SET. `~` with an
  // empty operand reaches Mongo as `$regex: Pattern.quote("")` = `\Q\E`, which
  // matches every string value of the field — so a half-built row silently
  // selected the whole event instead of nothing, and PBulkEdit's `liveCount > 0`
  // guard (the thing that keeps Apply disabled until a scope exists) stopped
  // guarding. `=` merely matched nothing, which is why this never showed.
  //
  // AFTER the coercion above, never before it: the "—" (is-empty) pick travels
  // as ("=", "") on a discrete field and only becomes "∅" inside this function.
  // Filtering first would have deleted "match the unset rows" from bulk edit and
  // export — see the same warning on restoreOriginalFilterKeys. The "∅"/"⊙"
  // carve-out below is what lets the coerced row through.
  return parsedFilters.filter(
    (f) => !isEmptyOperand(f.value) || isNoValueOperator(f.operator),
  );
}

// System fields that are valid API filters but not in field definitions
export const SYSTEM_FILTER_FIELDS = new Set(["guestId", "archived", "checkedIn", "checkedInAt"]);

/** Wire prefix every additional-field filter/sort key carries; `Field.name` is bare. */
const ADDITIONAL_FIELD_PREFIX = "additionalFields.";

export function parseFiltersApiRequest(
  filters: Filter[],
  allFields: Field[],
  { onUnresolvedKey = "drop" }: { onUnresolvedKey?: "drop" | "keep" } = {},
): Filter[] {
  if (!filters || filters?.length === 0) return [];

  const processedFilters: Filter[] = [];

  for (const filter of filters) {
    // System fields bypass field-definition validation. They must still get
    // the empty-equals coercion: the dashboard "not checked in" click-through
    // builds {key:'checkedIn',operator:'=',value:''} and an un-coerced ("", "=")
    // reaches the backend where Boolean.parseBoolean("") === false — matching
    // checked-OUT guests instead of never-scanned ones. Reuse
    // coerceEmptyEqualsOperator with CHECK_IN_STATUS (a sentinel dataType) so
    // ("=", "") → "∅" / ("≠", "") → "⊙" exactly like field-backed filters.
    if (SYSTEM_FILTER_FIELDS.has(filter.key)) {
      const rawValue =
        typeof filter.value === "string" ? filter.value.trim() : filter.value;
      const { operator, value } = coerceEmptyEqualsOperator(
        filter.operator,
        rawValue,
        DataType.CHECK_IN_STATUS,
      );
      processedFilters.push({ ...filter, operator, value });
      continue;
    }

    // Find the field definition for this filter. Saved filters exist in BOTH
    // stored shapes — the dialog's bare `tier` (restoreOriginalFilterKeys) and
    // the conditions dual-write's prefixed `additionalFields.tier` — so strip
    // the wire prefix before matching against Field.name, which is always bare.
    // Matching only the bare form dropped every prefixed saved filter even
    // while its field existed (PB-TJ-3, measured).
    const bareKey = filter.key.startsWith(ADDITIONAL_FIELD_PREFIX)
      ? filter.key.slice(ADDITIONAL_FIELD_PREFIX.length)
      : filter.key;
    const fieldDef = allFields.find((f) => f.name === bareKey);

    // PB-TJ-3: a filter key that resolves to no live field is dropped here —
    // silently. Dropping a RESTRICTING row WIDENS the result: a saved
    // `tier ≠ vip` guestlist filter went from hiding the VIPs to showing them
    // the moment an unrelated admin deleted the `tier` field, with no warning.
    // Every operator fails this way, because every filter row narrows.
    //
    // Callers that pass the UNFILTERED field set can opt into keeping the row
    // instead ({onUnresolvedKey:"keep"}): the backend already refuses a filter
    // naming a field it does not have (GuestService.validateFilterKeys →
    // `unknownFilterKey` 400), so keeping it turns a silent widening into an
    // explicit, nameable refusal the caller renders as a broken-filter banner.
    //
    // The default stays "drop" on purpose. A caller holding the ALLOWLIST-
    // filtered field set (a CUSTOM role's visible columns) cannot tell "field
    // deleted" from "field hidden from me", and failing a hidden-field filter
    // closed would break the guestlist for that role instead of widening it.
    // Only a caller with allFieldsUnfiltered may opt in.
    if (!fieldDef) {
      if (onUnresolvedKey === "keep") {
        processedFilters.push({
          ...filter,
          key: filter.key.startsWith(ADDITIONAL_FIELD_PREFIX)
            ? filter.key
            : `${ADDITIONAL_FIELD_PREFIX}${filter.key}`,
        });
      }
      continue;
    }

    // Apply prefix for additional fields, but only if not already prefixed
    const key =
      fieldDef.additionalField && !filter.key.startsWith("additionalFields.")
        ? `additionalFields.${filter.key}`
        : filter.key;

    // `normalizeFieldValue`, not `.trim()`: the write lanes strip U+200B and JS
    // `.trim()` does not, so a pasted operand ending in a zero-width space could
    // no longer match the (now clean) stored value — the very failure this whole
    // change set exists to close, one character wide.
    if (filter.value) {
      if (Array.isArray(filter.value)) {
        filter.value = filter.value.map((item) => normalizeFieldValue(item));
      } else {
        filter.value = normalizeFieldValue(filter.value);
      }
    }

    // Defense-in-depth: any ("=", "") that reaches here on a discrete field
    // becomes the "∅"/"⊙" emptiness operator (the table path already coerces
    // at restoreOriginalFilterKeys; this covers Filter[] built elsewhere).
    const { operator, value } = coerceEmptyEqualsOperator(
      filter.operator,
      filter.value,
      fieldDef.dataType,
    );

    // Expand a MULTI_SELECT array value into one scalar-string row per picked
    // value. The backend `Filter.value` is a single String and its JSON-B
    // (Yasson) binding silently keeps only the LAST element of a JSON array —
    // so an un-expanded multi-pick filter dropped every value but the last
    // (e.g. `diet in {Vegan, Veg}` only filtered by `Veg`). Emitting one row
    // per value lets the backend's same-key OR-grouping reconstruct the intent
    // ("=" → contains any / "≠" → identical to adding multiple ≠ rows).
    // Coerce PER element: the "" sentinel (the "—" is-empty pick, which now
    // coexists with real values in one dropdown) becomes its own "∅"/"⊙" OR-row
    // while real picks stay "=", so "M OR (unset)" is expressible. A whole-empty
    // array was already collapsed to scalar ∅ by coerce above.
    if (Array.isArray(value)) {
      for (const singleValue of value) {
        const perEl = coerceEmptyEqualsOperator(
          operator,
          singleValue,
          fieldDef.dataType,
        );
        processedFilters.push({
          ...filter,
          key,
          operator: perEl.operator,
          value: perEl.value,
        });
      }
    } else {
      processedFilters.push({ ...filter, key, operator, value });
    }
  }

  return processedFilters;
}

/**
 * Keys `normalizeCreationFields` must not rewrite. `username` and `code` are
 * login identity behind the partial UNIQUE index (eventId, username) — a
 * rewrite could collide two rows the caller believes are distinct; `mainGuest`
 * is an id, not text. Mirrors the same carve-out on the backend
 * (`GuestService.bulkUpdateByFilter`). `additionalFields` is handled below as a
 * map rather than a scalar.
 */
const SKIP_NORMALIZE_KEYS = new Set([
  "additionalFields",
  "username",
  "code",
  "mainGuest",
]);

/**
 * Shallow-normalize every string a guest form hands over: built-in scalars and
 * additionalFields values (arrays element-wise). Edge whitespace only —
 * internal spacing is content.
 *
 * `buildUpdateGuests` is a SECOND, fully independent write path: it never calls
 * `parseGuest`/`parseString`, so the normalization there does not reach it and
 * the guest-detail drawer — the highest-traffic single-guest edit surface —
 * would keep writing padded values.
 *
 * BOTH sides of the diff go through this. An earlier version normalized only the
 * incoming side, on the theory that the resulting diff would make the drawer
 * self-heal legacy padded rows. It did not: `GuestActions.tsx` computes its
 * dirty flag on the RAW objects, so Save stays greyed out for a purely padded
 * row and the branch never runs. What it DID produce was a padding-only diff on
 * every unrelated edit — and the backend reads any change to `email` as an
 * address change and clears the guest's `emailUndeliverable` suppression
 * (`GuestService.java:3620`), re-arming a hard-bounced address. It also made
 * the whole `additionalFields` map unequal, so one legacy padded key re-sent
 * every key. Comparing like with like removes all three. Legacy rows are the
 * backfill's job, not the drawer's.
 */
function normalizeCreationFields(fields: CreationFieldsRef): CreationFieldsRef {
  if (!fields) return fields;
  const out = { ...fields } as Record<string, unknown>;
  for (const [key, value] of Object.entries(out)) {
    if (SKIP_NORMALIZE_KEYS.has(key)) continue;
    if (typeof value === "string") out[key] = normalizeFieldValue(value);
  }
  const af = fields.additionalFields;
  if (af && typeof af === "object" && !Array.isArray(af)) {
    const nextAf: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(af)) {
      if (typeof value === "string") {
        nextAf[key] = normalizeFieldValue(value);
      } else if (Array.isArray(value)) {
        // DROPPED, not normalized into place — the same rule `sanitizeGuest`
        // applies on the create lane, which says of itself "Update paths do not
        // pass here". Mapping the elements is precisely what turns `[" "]` into
        // `[""]`, and Mongo matches an array ELEMENT-WISE, so a stored
        // `["VIP",""]` reads as blank and the guest who picked VIP leaves every
        // "has a value" audience. Every backend door now rejects such an element
        // unconditionally, so without the drop this drawer would 400 on a value
        // it sent happily the day before.
        //
        // Applied to BOTH sides of the diff (this function normalizes the form
        // and the stored record alike), so a legacy `["VIP",""]` still compares
        // equal to itself and is not re-sent on an unrelated edit — healing it
        // is the backfill's job, not the drawer's.
        nextAf[key] = value
          .map((el) => (typeof el === "string" ? normalizeFieldValue(el) : el))
          .filter((el) => el !== "");
      } else {
        // IMAGE objects and anything else the drawer round-trips untouched.
        nextAf[key] = value;
      }
    }
    out.additionalFields = nextAf;
  }
  return out as unknown as CreationFieldsRef;
}

/**
 * Whether the drawer has anything to send — the SAME question
 * {@link buildUpdateGuests} answers, asked once.
 *
 * The dirty flag used to be its own `JSON.stringify` comparison on the RAW form
 * objects, while the payload builder normalizes both sides. Two answers to one
 * question: a padding-only edit lit up Save, produced an empty payload, skipped
 * the request, and then settled the form as "saved" — a success message for a
 * write that never happened.
 *
 * The empty-`additionalFields` arm mirrors the save path's own guard: an IMAGE
 * upload is persisted at confirm time, so a save right after it can diff to a
 * lone empty map once the echoed value is stripped, and sending that would earn
 * a `noChanges` failure from the backend.
 */
export function hasGuestChanges(
  creationFields: CreationFieldsRef,
  creationFieldsDb: CreationFieldsRef,
): boolean {
  const diff = buildUpdateGuests(creationFields, creationFieldsDb);
  const keys = Object.keys(diff);
  if (keys.length === 0) return false;
  return !(
    keys.length === 1 &&
    diff.additionalFields &&
    Object.keys(diff.additionalFields).length === 0
  );
}

export function buildUpdateGuests(
  creationFieldsRaw: CreationFieldsRef,
  creationFieldsDbRaw: CreationFieldsRef,
): Guest {
  const creationFields = normalizeCreationFields(creationFieldsRaw);
  const creationFieldsDb = normalizeCreationFields(creationFieldsDbRaw);
  const updatedGuest: Partial<Guest> = {};

  // Compare each field and only add to updatedGuest if changed
  if (creationFields?.firstName !== creationFieldsDb?.firstName) {
    updatedGuest.firstName = creationFields?.firstName;
  }

  if (creationFields.lastName !== creationFieldsDb.lastName) {
    updatedGuest.lastName = creationFields.lastName;
  }

  if (creationFields.gender !== creationFieldsDb.gender) {
    if (creationFields.gender === "") {
      // "—" (not-set): a bare empty gender means "no change" on the partial-
      // update path, so clear it explicitly via $unset.
      (updatedGuest.clearFields ??= []).push("gender");
    } else {
      updatedGuest.gender = creationFields.gender as unknown as Gender; // Type cast since Gender is an enum
    }
  }

  if (creationFields.title !== creationFieldsDb.title) {
    updatedGuest.title = creationFields.title;
  }

  if (creationFields.company !== creationFieldsDb.company) {
    updatedGuest.company = creationFields.company;
  }

  if (creationFields.language !== creationFieldsDb.language) {
    updatedGuest.language = creationFields.language;
  }

  if (creationFields.email !== creationFieldsDb.email) {
    updatedGuest.email = creationFields.email;
  }

  if (creationFields.phone !== creationFieldsDb.phone) {
    updatedGuest.phone = creationFields.phone;
  }

  if (creationFields.mobile !== creationFieldsDb.mobile) {
    updatedGuest.mobile = creationFields.mobile;
  }

  if (creationFields.address !== creationFieldsDb.address) {
    updatedGuest.address = creationFields.address;
  }

  if (creationFields.address2 !== creationFieldsDb.address2) {
    updatedGuest.address2 = creationFields.address2;
  }

  if (creationFields.zipCode !== creationFieldsDb.zipCode) {
    updatedGuest.zipCode = creationFields.zipCode;
  }

  if (creationFields.city !== creationFieldsDb.city) {
    updatedGuest.city = creationFields.city;
  }

  if (creationFields.country !== creationFieldsDb.country) {
    updatedGuest.country = creationFields.country;
  }

  if (creationFields.state !== creationFieldsDb.state) {
    updatedGuest.state = creationFields.state as GuestState; // Type cast to GuestState
  }

  if (creationFields.emailOptIn !== creationFieldsDb.emailOptIn) {
    // Tri-state: "true"/"false" → boolean. "" (the "—" option) → null AND an
    // explicit clear, since a bare null means "no change" on the backend's
    // partial-update path — clearFields triggers a $unset instead.
    const optIn = parseTriStateBoolean(creationFields.emailOptIn);
    updatedGuest.emailOptIn = optIn;
    if (optIn === null) (updatedGuest.clearFields ??= []).push("emailOptIn");
  }

  // SMS channel wave: the SMS consent's twin of the arm above — minus the
  // clear. `?? ""` on both sides — a create-form seed carries no `smsOptIn`,
  // and "absent" and "not set" are the same answer. Only a flip to Yes / No is
  // sent: the backend's CLEARABLE_FIELDS keeps smsOptIn out on purpose (strict
  // consent, every flip writes a consent row), so "" is no change, never a
  // clearFields entry (guestClearFields.ts).
  if ((creationFields.smsOptIn ?? "") !== (creationFieldsDb.smsOptIn ?? "")) {
    const smsOptIn = parseTriStateBoolean(creationFields.smsOptIn ?? "");
    if (smsOptIn !== null) updatedGuest.smsOptIn = smsOptIn;
  }

  if (creationFields.jobTitle !== creationFieldsDb.jobTitle) {
    updatedGuest.jobTitle = creationFields.jobTitle;
  }

  if (creationFields.username !== creationFieldsDb.username) {
    updatedGuest.username = creationFields.username;
  }

  if (creationFields.code !== creationFieldsDb.code) {
    updatedGuest.code = creationFields.code;
  }

  if (creationFields.mainGuest !== creationFieldsDb.mainGuest) {
    updatedGuest.mainGuest = creationFields.mainGuest;
  }

  if (creationFields.guestType !== creationFieldsDb.guestType) {
    updatedGuest.guestType = creationFields.guestType as GuestType; // Type cast to GuestType
  }

  // additionalFields: a PER-KEY delta, never the whole map.
  //
  // This used to send every custom value as soon as any ONE key differed. The
  // drawer seeds its form from the guest copy it was opened on, so a save from
  // a stale copy re-sent every other key at its old value — and the batch
  // endpoint MERGES key by key, so a value the door staff had changed in the
  // meantime (a device filter keyed on a custom field, say) was quietly put
  // back. Only the keys this edit actually changed may travel:
  //
  //  - a key is CHANGED when its normalized form value differs from the
  //    normalized stored value — but two blank shapes (`undefined` / `null` /
  //    `""` / `[]`) are the same stored `null` and never diff against each
  //    other, or a never-set field would ship `""` on every unrelated save;
  //  - a cleared text (`""`) / multi (`[]`) over a real value IS a change and
  //    rides as that blank — the server writes `null` for it (drawer lane:
  //    `$set null`; the inline lane's `clearFields` `$unset` is unaffected);
  //  - a key that exists only in the baseline is left alone — an omitted key
  //    is "no change" on the partial-update path, and this lane has no way to
  //    express a delete it was never asked for;
  //  - IMAGE echoes are stripped from BOTH sides first (see
  //    `stripPersistedImageEchoes`), so a fresh upload never counts as a diff.
  //
  // `additionalFields` is set ONLY when at least one key changed. An empty map
  // would be read by `hasGuestChanges` as "nothing to send" anyway, but an
  // absent key is the honest shape and keeps the payload minimal.
  if (creationFields.additionalFields || creationFieldsDb.additionalFields) {
    const [current, baseline] = stripPersistedImageEchoes(
      creationFields.additionalFields || {},
      creationFieldsDb.additionalFields || {},
    );
    const delta: Record<string, unknown> = {};
    for (const key of Object.keys(current)) {
      const cur = current[key];
      const base = baseline[key];
      if (isBlankAdditionalFieldValue(cur) && isBlankAdditionalFieldValue(base)) {
        continue;
      }
      // A form `undefined` over a stored value is DROPPED, not sent as a
      // clear. No drawer input can emit it (`DataTypeInput` hands back
      // `string | string[]`), so it is a seeding accident, not an intent —
      // coercing it to "" would `$set null` on a path nobody drives. And it
      // could not travel anyway: `{k: undefined}` is JSON-omitted, which the
      // merge reads as "no change" — leaving it in the delta would only light
      // up Save (`hasGuestChanges`) for a payload the server nets to
      // `noChanges`. Explicit, and pinned (update-lane-af-delta.test.ts).
      if (cur === undefined) continue;
      if (JSON.stringify(cur) !== JSON.stringify(base)) delta[key] = cur;
    }
    if (Object.keys(delta).length > 0) updatedGuest.additionalFields = delta;
  }

  return updatedGuest as Guest;
}

/**
 * IMAGE additional-field values are persisted server-side at upload-confirm
 * time (presign→confirm mints `{key,contentType,size}` and writes
 * guest.additionalFields immediately) — so an AfImageValue OBJECT sitting in
 * the popup's form state is always just an ECHO of what the backend already
 * stores. The backend merge guard ignores a deep-equal echo, which means a
 * save whose only "change" is that echo nets ZERO modifications and the
 * batch endpoint fails the guest with `noChanges`. Strip echoed image
 * objects from BOTH the diff and the payload; only the "" clear (a real
 * mutation the backend applies: object delete + storage release) participates.
 *
 * A key is excluded — from BOTH sides, so it can neither ride the payload nor
 * register as a phantom diff — when the CURRENT value is an image object
 * (fresh upload echo / replace echo), or when the current key is gone while
 * the baseline still holds an image object (nothing meaningful to send — an
 * omitted key is "no change" on the partial-update path). A current "" over a
 * baseline object is a real clear and stays in.
 *
 * Returns fresh `[current, baseline]` maps; inputs are never mutated.
 */
function stripPersistedImageEchoes(
  currentIn: Record<string, unknown>,
  baselineIn: Record<string, unknown>,
): [Record<string, unknown>, Record<string, unknown>] {
  const excluded = (key: string): boolean =>
    isAfImageValue(currentIn[key]) ||
    (currentIn[key] === undefined && isAfImageValue(baselineIn[key]));
  const current: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(currentIn)) {
    if (!excluded(key)) current[key] = value;
  }
  const baseline: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(baselineIn)) {
    if (!excluded(key)) baseline[key] = value;
  }
  return [current, baseline];
}

export function getDates(event: Event) {
  if (!event.start || !event.end) return [];

  const startDate = new Date(event.start);
  const endDate = new Date(event.end);

  // Generate all dates between start and end
  const dates = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const day = String(currentDate.getDate()).padStart(2, "0");
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    dates.push(`${day}.${month}`);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
}

export function parsePngName(platform: string) {
  switch (platform) {
    case "android":
      return "Android.png";
    case "apple":
      return "Apple.png";
    case "apple-music":
      return "Apple%20Music.png";
    case "apple-podcasts":
      return "Apple%20Podcasts.png";
    case "artstation":
      return "ArtStation.png";
    case "baidu":
      return "Baidu.png";
    case "behance":
      return "Behance.png";
    case "boosty":
      return "Boosty.png";
    case "deviantart":
      return "DevianArt.png";
    case "discord":
      return "Discord.png";
    case "dprofile":
      return "Dprofile.png";
    case "dribbble":
      return "Dribbble.png";
    case "dzen":
      return "Dzen.png";
    case "facebook":
      return "Facebook.png";
    case "figma":
      return "Figma.png";
    case "github":
      return "Github.png";
    case "gmail":
      return "Gmail.png";
    case "google":
      return "Google.png";
    case "google-meets":
      return "Google%20Meets.png";
    case "google-play":
      return "Google%20Play.png";
    case "google-podcast":
      return "Google%20Podcast.png";
    case "imo":
      return "Imo.png";
    case "instagram":
      return "Instagram.png";
    case "kickstarter":
      return "Kickstarter.png";
    case "line":
      return "Line.png";
    case "linkedin":
      return "LinkedIn.png";
    case "medium":
      return "Medium.png";
    case "messenger":
      return "Messenger.png";
    case "microsoft-teams":
      return "Microsoft%20Teams.png";
    case "notion":
      return "Notion.png";
    case "ok":
      return "OK.png";
    case "ok-sign":
      return "OK%20(Only%20sign).png";
    case "onlyfans":
      return "OnlyFans.png";
    case "patreon":
      return "Patreon.png";
    case "pinterest":
      return "Pinterest.png";
    case "product-hunt":
      return "Product%20Hunt%20.png";
    case "quora":
      return "Quora.png";
    case "reddit":
      return "Reddit.png";
    case "signal":
      return "Signal.png";
    case "sina-weibo":
      return "Sina%20Weibo.png";
    case "skype":
      return "Skype.png";
    case "slack":
      return "Slack.png";
    case "snapchat":
      return "Snapchat.png";
    case "social-icons":
      return "Social%20Icons.png";
    case "soundcloud":
      return "SoundCloud.png";
    case "spotify":
      return "Spotify.png";
    case "stack-overflow":
      return "Stack%20Overflow.png";
    case "telegram":
      return "Telegram.png";
    case "telegram-sign":
      return "Telegram%20(Only%20sign).png";
    case "threads":
      return "Threads.png";
    case "tiktok":
      return "TikTok.png";
    case "tinder":
      return "Tinder.png";
    case "tumblr":
      return "Tumblr.png";
    case "twitch":
      return "Twitch.png";
    case "viber":
      return "Viber.png";
    case "vimeo":
      return "Vimeo.png";
    case "vk":
      return "VK.png";
    case "vk-music":
      return "VK%20Music.png";
    case "vk-sign":
      return "VK%20(Only%20sign).png";
    case "wechat":
      return "WeChat.png";
    case "whatsapp":
      return "WhatsApp.png";
    case "x-twitter":
      return "X%20ex%20Twitter.png";
    case "xing":
      return "Xing.png";
    case "yandex-music":
      return "Yandex%20Music.png";
    case "yelp":
      return "Yelp.png";
    case "youtube":
      return "YouTube.png";
    case "youtube-music":
      return "Youtube%20Music.png";
    case "youtube-shorts":
      return "YouTube%20Shorts.png";
    case "zoom":
      return "Zoom.png";
    default:
      return `${platform}.png`;
  }
}
